import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { ArrowLeft, Search, Building2, Check, UserPlus } from "lucide-react";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";


export default function ProfessionalsSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [city, setCity] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [followingIds, setFollowingIds] = useState(new Set());
  const [followBusy, setFollowBusy] = useState(null);
  const sentImpressions = useRef(new Set());

  useEffect(() => {
    api.get("/me/following-professionals?limit=100")
      .then((r) => setFollowingIds(new Set((r.data.following || []).map((it) => it.professional?.user_id).filter(Boolean))))
      .catch(() => {});
  }, []);

  const doSearch = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (category.trim()) params.set("category", category.trim());
      if (city.trim()) params.set("city", city.trim());
      const r = await api.get(`/professionals/search?${params.toString()}`);
      setResults(r.data.results || []);
    } catch (e) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [q, category, city]);

  // Debounced auto-search on q change (≥2 chars)
  useEffect(() => {
    if (!q && !category && !city) { setResults([]); return; }
    const t = setTimeout(doSearch, 300);
    return () => clearTimeout(t);
  }, [q, category, city, doSearch]);

  // Analytics: batch impression tracking (once per pro per session)
  useEffect(() => {
    const fresh = results.filter((r) => !sentImpressions.current.has(r.user_id));
    if (fresh.length === 0) return;
    fresh.forEach((r) => sentImpressions.current.add(r.user_id));
    api.post("/professionals/track", {
      events: fresh.map((r) => ({ professional_user_id: r.user_id, event_type: "impression", source: "search" })),
    }).catch(() => {});
  }, [results]);

  const openPro = (proId) => {
    api.post("/professionals/track", {
      events: [{ professional_user_id: proId, event_type: "click", source: "search" }],
    }).catch(() => {});
    navigate(`/app/pro/${proId}`);
  };

  const quickFollow = async (e, pro) => {
    e.stopPropagation();
    if (followingIds.has(pro.user_id) || followBusy) return;
    setFollowBusy(pro.user_id);
    try {
      await api.post(`/professional/${pro.user_id}/follow`, { source: "search" });
      setFollowingIds((prev) => new Set([...prev, pro.user_id]));
      window.dispatchEvent(new Event("ade-following-changed"));
      toast.success(`Collegato a ${pro.professional_profile?.professional_name || pro.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setFollowBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="professionals-search-page">
      <Seo title="Cerca ADE Pro · ADE" description="Trova attività e professionisti ADE Pro su ADE" />
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b flex items-center gap-3 px-4 py-3">
        <button onClick={() => navigate(-1)} className="p-1" data-testid="prosearch-back">
          <ArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
        <h1 className="text-sm font-semibold text-gray-800 flex-1">Cerca ADE Pro</h1>
      </div>

      <div className="px-4 py-4 space-y-3">
        <div className="flex items-center gap-2 bg-gray-100 rounded-full px-4 py-2.5">
          <Search className="w-4 h-4 text-gray-400" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Nome attività, @username, servizio…"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
            data-testid="prosearch-input"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Categoria"
            className="w-full text-sm px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 outline-none"
            data-testid="prosearch-category"
          />
          <input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Città"
            className="w-full text-sm px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 outline-none"
            data-testid="prosearch-city"
          />
        </div>
      </div>

      {loading && <p className="text-center text-gray-400 text-sm py-4">Ricerca in corso…</p>}
      {!loading && (q || category || city) && results.length === 0 && (
        <p className="text-center text-gray-400 text-sm py-8" data-testid="prosearch-empty">Nessun risultato</p>
      )}

      <div className="px-4 pb-6 space-y-2">
        {results.map((r) => {
          const pp = r.professional_profile || {};
          const isFollowing = followingIds.has(r.user_id);
          return (
            <div
              key={r.user_id}
              onClick={() => openPro(r.user_id)}
              className="w-full flex items-center gap-3 p-3 rounded-2xl border border-gray-100 hover:bg-gray-50 text-left cursor-pointer"
              data-testid={`prosearch-result-${r.user_id}`}
            >
              <img
                src={r.picture || "/images/default-avatar.png"}
                alt=""
                className="w-12 h-12 rounded-xl object-cover bg-gray-100 flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="text-sm font-semibold text-gray-900 truncate">{pp.professional_name || r.name}</p>
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200">
                    <Building2 className="w-2.5 h-2.5" /> PRO
                  </span>
                </div>
                <p className="text-[11px] text-gray-500 truncate">
                  {pp.category}{pp.subcategory ? ` · ${pp.subcategory}` : ""}{pp.city ? ` · ${pp.city}` : ""}
                </p>
              </div>
              {isFollowing ? (
                <span
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 flex-shrink-0"
                  data-testid={`quick-followed-${r.user_id}`}
                >
                  <Check className="w-3 h-3" /> Collegato
                </span>
              ) : (
                <button
                  onClick={(e) => quickFollow(e, r)}
                  disabled={followBusy === r.user_id}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex-shrink-0 active:scale-95 transition-transform"
                  data-testid={`quick-follow-btn-${r.user_id}`}
                >
                  <UserPlus className="w-3 h-3" /> {followBusy === r.user_id ? "…" : "Segui"}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
